/**
 * A room's fiscal calendar (migration 0057, ADR-0040) for the admin forms: the
 * fiscal year ends on the LAST day of a month (December 31 = the calendar year,
 * the default) and the room's "today" is read in an IANA timezone (default UTC).
 * The DD checklist's period requests ("FY2025", "FY2025 Q1" …) are generated from
 * it once, when the checklist is created.
 */

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** The last day of `month` (1–12) as stored: February is 28 ("end of February"). */
export function fiscalYearEndDay(month: number): number {
  return new Date(Date.UTC(2001, month, 0)).getUTCDate();
}

/** "June 30", "February 28/29", "December 31 (calendar year)". */
export function fiscalYearEndLabel(month: number): string {
  const name = MONTHS[month - 1] ?? '';
  if (month === 2) return `${name} 28/29`;
  const label = `${name} ${fiscalYearEndDay(month)}`;
  return month === 12 ? `${label} (calendar year)` : label;
}

/** The selectable fiscal year ends, December (the default) first. */
export const FISCAL_YEAR_END_MONTHS: readonly number[] = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

/** The default room timezone. */
export const DEFAULT_ROOM_TIMEZONE = 'UTC';

const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
] as const;

/** The browser's own IANA timezone, or null when it cannot be read. */
function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** The timezone choices: the common US zones + UTC, plus the browser's zone and
 * the room's current one when they are not already listed. */
export function timeZoneOptions(current?: string | null): string[] {
  const out: string[] = [...COMMON_TIMEZONES];
  for (const extra of [browserTimeZone(), current ?? null]) {
    if (extra && !out.includes(extra)) out.push(extra);
  }
  return out;
}
